import { Collapse } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PropTypes from 'prop-types';
import React, { forwardRef, memo, useEffect, useRef, useState } from 'react';

const useStyles = makeStyles(theme => ({
  licenceCard: {
    backgroundColor: theme.palette.veryDarkGrey,
    borderRadius: '5px',
    padding: '16px 20px',
    marginBottom: '12px',
    color: theme.palette.primaryColor,
    border: 'none',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  focused: {
    boxShadow: `0 0 15px ${theme.palette.shadowBlue}`,
    opacity: 1,
  },
  blurred: {
    opacity: 0.7,
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: theme.palette.primaryColor,
    margin: 0,
  },
  subtitle: {
    fontSize: '0.85rem',
    color: theme.palette.thirdColor,
    marginTop: '4px',
    marginBottom: '14px',
  },
  bulletPoint: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
  bulletIcon: {
    marginRight: '8px',
    color: theme.palette.primaryColor,
    fontSize: '6px',
    marginTop: '7px',
  },
  bulletText: {
    color: theme.palette.primaryColor,
    fontSize: '0.85rem',
    lineHeight: '1.3',
  },
  bulletTextTruncated: {
    color: theme.palette.primaryColor,
    fontSize: '0.85rem',
    lineHeight: '1.3',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  boldText: {
    fontWeight: 'bold',
    color: theme.palette.primaryColor,
  },
  expandButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    color: theme.palette.secundaryColor,
    fontSize: '0.8rem',
    marginTop: '8px',
    fontWeight: 'normal',
  },
  expandButtonText: {
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      background: `linear-gradient(45deg, ${theme.palette.gradientStartDarkGrey} 30%, ${theme.palette.gradientEndDarkGrey} 90%)`,
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  expandIcon: {
    marginLeft: '4px',
    fontSize: '16px',
    transition: 'transform 0.3s ease',
  },
  expanded: {
    transform: 'rotate(180deg)',
  },
  content: {
    padding: '0',
    margin: '0',
    listStyle: 'none',
  },
  sectionTitle: {
    color: theme.palette.primaryColor,
    fontWeight: 'bold',
    fontSize: '0.9rem',
    marginTop: '14px',
    marginBottom: '8px',
  },
  contentWrapper: {
    overflow: 'hidden',
  },
}));

const LicenceData = memo(
  forwardRef(({ title, subtitle, content, isFocused, onFocusChange }, ref) => {
    const [expanded, setExpanded] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const collapseRef = useRef(null);
    const classes = useStyles();

    const visibleContent = expanded || isFocused ? content : [];
    const previewContent = content.length > 0 ? [content[0]] : [];

    const toggleExpand = e => {
      e.stopPropagation();
      setIsAnimating(true);
      setExpanded(!expanded);
      if (!expanded) {
        onFocusChange(title);
      }
    };

    useEffect(() => {
      if (isFocused) {
        setExpanded(true);
        setIsAnimating(true);
        const element = document.getElementById(
          `licenceCard-${title.replace(/\s/g, '')}`
        );
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        setExpanded(false);
      }
    }, [isFocused, title]);

    const handleTransitionEnd = () => {
      setIsAnimating(false);
    };

    const renderRichText = item => {
      if (typeof item === 'string') {
        return item;
      }

      if (Array.isArray(item)) {
        return item.map((part, idx) => {
          if (typeof part === 'string') {
            return <React.Fragment key={idx}>{part}</React.Fragment>;
          } else if (part.type === 'bold') {
            return (
              <span key={idx} className={classes.boldText}>
                {part.text}
              </span>
            );
          }
          return null;
        });
      }

      return item;
    };

    const renderFullContent = () => {
      if (visibleContent.length === 0) return null;

      return visibleContent.map((item, index) => {
        if (typeof item === 'object' && item.type === 'title') {
          return (
            <div key={index}>
              <h3 className={classes.sectionTitle}>{item.text}</h3>
            </div>
          );
        }

        return (
          <li key={index} className={classes.bulletPoint}>
            <span className={classes.bulletIcon}>•</span>
            <span className={classes.bulletText}>{renderRichText(item)}</span>
          </li>
        );
      });
    };

    const renderPreview = () => {
      if (previewContent.length === 0) return null;

      return previewContent.map((item, index) => {
        if (typeof item === 'object' && item.type === 'title') {
          return (
            <div key={index}>
              <h3 className={classes.sectionTitle}>{item.text}</h3>
            </div>
          );
        }

        return (
          <li key={index} className={classes.bulletPoint}>
            <span className={classes.bulletIcon}>•</span>
            <span className={classes.bulletTextTruncated}>
              {renderRichText(item)}
            </span>
          </li>
        );
      });
    };

    return (
      <div
        id={`licenceCard-${title.replace(/\s/g, '')}`}
        className={`${classes.licenceCard} ${
          isFocused ? classes.focused : expanded ? '' : classes.blurred
        }`}
        onClick={() => !isFocused && onFocusChange(title)}
        ref={ref}
      >
        <h2 className={classes.title}>{title}</h2>
        <h4 className={classes.subtitle}>{subtitle}</h4>

        {!expanded && !isAnimating && (
          <ul className={classes.content}>{renderPreview()}</ul>
        )}

        <div className={classes.contentWrapper}>
          <Collapse
            ref={collapseRef}
            in={expanded}
            timeout={300}
            onExited={handleTransitionEnd}
            onEntered={handleTransitionEnd}
          >
            <ul className={classes.content}>{renderFullContent()}</ul>
          </Collapse>
        </div>

        <div className={classes.expandButton}>
          <span className={classes.expandButtonText} onClick={toggleExpand}>
            {expanded ? 'Ver menos' : 'Ver más'}
            <ExpandMoreIcon
              className={`${classes.expandIcon} ${
                expanded ? classes.expanded : ''
              }`}
            />
          </span>
        </div>
      </div>
    );
  })
);

LicenceData.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  content: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.array,
      PropTypes.shape({
        type: PropTypes.string,
        text: PropTypes.string,
      }),
    ])
  ).isRequired,
  isFocused: PropTypes.bool,
  onFocusChange: PropTypes.func,
};

LicenceData.defaultProps = {
  isFocused: false,
  onFocusChange: () => {},
};

export default LicenceData;
